import { RegisterOptions, SetValueConfig, Validate } from 'react-hook-form'

export const REQUIRED_MESSAGE = gettext('This field is required.')
export const requiredRule: RegisterOptions = { required: REQUIRED_MESSAGE }
export const listNotEmpty: Validate = (value: string[]) => value.length > 0 || REQUIRED_MESSAGE

export const shouldValidateAndDirtyConfig: SetValueConfig = {
  shouldDirty: true,
  shouldValidate: true,
}

interface ValueLengthValidatorArgs {
  value: string
  minLengthRequired?: number
  maxLengthRequired?: number
}

export const valueLengthValidator = ({
  value,
  minLengthRequired,
  maxLengthRequired,
}: ValueLengthValidatorArgs): string | true => {
  if (minLengthRequired && value && value.length < minLengthRequired) {
    return interpolate(
      ngettext(
        'Ensure this value has at least %(minLengthRequired)s character (it has %(valueLength)s).',
        'Ensure this value has at least %(minLengthRequired)s characters (it has %(valueLength)s).',
        minLengthRequired
      ),
      { minLengthRequired: minLengthRequired.toString(), valueLength: value.length.toString() },
      true
    )
  }

  if (maxLengthRequired && value && value.length > maxLengthRequired) {
    return interpolate(
      ngettext(
        'Ensure this value has at most %(maxLengthRequired)s character (it has %(valueLength)s).',
        'Ensure this value has at most %(maxLengthRequired)s characters (it has %(valueLength)s).',
        maxLengthRequired
      ),
      { maxLengthRequired: maxLengthRequired.toString(), valueLength: value.length.toString() },
      true
    )
  }

  return true
}

export const QR_BILL_MEMBERSHIP_NUMBER_INVALID_MESSAGE = gettext(
  'The membership number can only contain numbers'
)

export const qrBillMembershipNumberRules: RegisterOptions = {
  pattern: {
    value: /^\d+$/,
    message: QR_BILL_MEMBERSHIP_NUMBER_INVALID_MESSAGE,
  },
  validate: value => valueLengthValidator({ value, minLengthRequired: 6, maxLengthRequired: 6 }),
}

export const QR_BILL_IBAN_INVALID_MESSAGE = gettext('This QR-IBAN format is invalid')

// A QR-IBAN is a Swiss/Liechtenstein IBAN whose 5-digit bank clearing number (the IID) falls
// within the 30000-31999 range reserved for QR-bill, per the backend's validate_qr_bill_iban.
// The trailing 12-character account number is alphanumeric (Swiss BBAN format), not numeric-only.
const QR_IBAN_FORMAT = /^(CH|LI)\d{2}3[01]\d{3}[A-Z0-9]{12}$/

// ISO 7064 MOD 97-10 checksum, mirroring stdnum.iban.is_valid() on the backend: move the first 4
// characters to the end, map letters to A=10..Z=35, then the resulting number mod 97 must equal 1.
const isValidIbanChecksum = (iban: string): boolean => {
  const rearranged = iban.slice(4) + iban.slice(0, 4)
  let remainder = 0
  for (const char of rearranged) {
    const numeral = /[0-9]/.test(char) ? char : (char.charCodeAt(0) - 55).toString()
    for (const digit of numeral) {
      remainder = (remainder * 10 + Number(digit)) % 97
    }
  }
  return remainder === 1
}

export const qrBillIbanRules: RegisterOptions = {
  validate: (value: string) => {
    if (!value) return true
    const sanitizedIban = value.replace(/\s/g, '').toUpperCase()
    return (
      (QR_IBAN_FORMAT.test(sanitizedIban) && isValidIbanChecksum(sanitizedIban)) ||
      QR_BILL_IBAN_INVALID_MESSAGE
    )
  },
}
